
import { CSSProperties } from "@mui/material/styles/createTypography"
import { IdentityClient } from "api-clients/IdentityClient"
import { PlansClient } from "api-clients/PlansClient"
import { LogoType, ProductInfoService } from "services/ProductInfoService"
import Container from "components/layout/Container"
import Grid from 'components/layout/grid/Grid'
import Item from 'components/layout/grid/Item'
import Computer, { IComputerProps } from "../../components/objects/computer/Computer"
import Title from "components/labels/Title"
import Subtitle from "components/labels/SubHeading"
import Page from "components/layout/Page"
import Section from "components/layout/Section"
import Button from "components/buttons/Button"
import Box from "components/layout/box/Box"
import Heading from "components/labels/Heading"
import Text from 'components/labels/Text';

import CardFeature from 'views/home/CardFeature'
import { ColorsUtil, MyTheme } from "MyTheme"
import { Avatar, Chip, Divider, FormControl, FormControlLabel, FormHelperText, FormLabel, IconButton, List, ListItem, ListItemAvatar, ListItemText, MenuItem, Paper, Radio, RadioGroup, Select, Stack, Step, StepButton, StepLabel, Stepper, useMediaQuery, Zoom } from "@mui/material"
import React, { useEffect, useRef, useState, VideoHTMLAttributes } from "react"
import useVisibility from "hooks/UseVisibility"
import Animation from "components/animations/Animation"
import SubHeading from "components/labels/SubHeading"
import TitleBlock from "components/layout/TitleBlock"
import Icon from "components/icons/Icon"
import { VideoMarker, VideoMarkersController } from "utils/VideoUtil"
import Modal from "components/modals/Dialog"
import Dialog from "components/modals/Dialog"
import ExpansionPanel from "components/transitions/expansionPanel/ExpansionPanel"
import Link from "components/buttons/Link"
import { Bootstrapper } from "Bootstrapper"
import LoadingButton from "components/buttons/LoadingButton"
import { DialogUtil } from "utils/DialogUtil"
import { NavLink, useNavigate } from "react-router-dom"
import { BadRequestError, UnauthorizedError } from "errors/ApplicationErrors"



const DownloadsButton = (props: any) => {
    const {beta, hideChips, hideProductOptions, hideLogo, isMacDevice, isDesktopDownloadOverride, requiresAccount, onDownloadChange, onCompleted, linkOnly, hideDetailsLink, ...rest} = props;
    const prodInfoService = Bootstrapper.getInstance<ProductInfoService>(ProductInfoService);
    const theme = MyTheme();
    const bg1 = theme.palette.grey[200];
    const bg2 = theme.palette.grey['100'];
    const dialogUtils = new DialogUtil();
    const navigate = useNavigate();
    const [isMacOsDetected, setIsMacOsDetected] = useState<boolean>();
    const [canSubmit, setCanSubmit] = useState<boolean>(true);
    const [containedIsDesktopDownload, setContainedIsDesktopDownload] = useState<boolean>(true);
    const isContained = isDesktopDownloadOverride == null;

    const isDesktopDownload = isContained ? containedIsDesktopDownload : isDesktopDownloadOverride;

    useEffect(() => {
        const isMac = prodInfoService.isMacOs();
        setIsMacOsDetected(isMac);
        setContainedIsDesktopDownload(!isMac);
    }, []);

    const downloadLatestAsync = async(isDesktopDownload: boolean = true) => {
        try{
            setCanSubmit(false);
            var os = prodInfoService.getOS();
            if(isDesktopDownload && os !== null && os !== 'windows'){
                var result = false;
                const confirm = await dialogUtils.confirmDialogAsync("This Add-in requires a Windows operating system. Are you sure you want to continue with your download?")
                if(confirm)
                    result = await prodInfoService.downLoadInstallerAsync(beta, true);
                
                onCompleted && onCompleted(result);
            }
            else{
                const result = await prodInfoService.downLoadInstallerAsync(beta, isDesktopDownload).catch((e: Error) => {
                    if (e instanceof UnauthorizedError) {
                        dialogUtils.confirmDialog("This download requires a free account. Register now?", async(confirmed) => {
                            if(confirmed){
                                navigate('/register');
                            }
                        });
                    }
                    else if(e instanceof BadRequestError){
                        dialogUtils.confirmDialog(e.message, () => false);
                    }
                    else 
                        throw e;
    
                });
                onCompleted && onCompleted(result);
            }    
        }
        finally{
            setCanSubmit(true);
        }
    }

    const onInstallerChange = (isDesktop: boolean) => {
        if(isContained)
            setContainedIsDesktopDownload(isDesktop);
        onDownloadChange && onDownloadChange(isDesktop);
    }

    const selectOfficeAddin = !isDesktopDownload;

    const chips = ["Windows"]
    if(selectOfficeAddin)
        chips.push("Mac")

    const displayErrorText = isDesktopDownload && isMacOsDetected; 
    const logoUrl = prodInfoService.getLogoUrl(LogoType.WithTextTransparent);
    const homeIcon = <Box mb={0} display='flex' alignItems='center' width={'250px'}><img alt="Zibbit logo" src={logoUrl}/></Box>

    return(
        <>
            {linkOnly && <Text>Need the software? Download it <Link component={NavLink} to="/downloads">here</Link></Text>}

            {!linkOnly && <Box display="flex" flex='1 1 0' flexDirection='column'>
                    {!hideLogo && <Stack pb={2} direction="row" alignItems="center">
                        {homeIcon}
                    </Stack>}
                    {!hideProductOptions && <>
                        {displayErrorText && <FormHelperText error>This only works on Windows devices</FormHelperText>}   
                    <Box flexWrap='no-wrap' border='1px solid' disabled={!canSubmit} display='flex' flex='1 1 0'>
                        <LoadingButton sx={{borderRadius: '0px', mr: 0}}  loading={!canSubmit} onClick={() => downloadLatestAsync(isDesktopDownload)} {...rest}>Download{beta ? "-Beta" : ""}</LoadingButton>
                        <Select onChange={(e) => onInstallerChange(!Boolean(e.target.value))} value={isDesktopDownload ? 0 : 1} disableUnderline variant="standard"  
                            sx={{
                                flex: '1 1 0',
                                pl: 1, pr: 1,
                                '& .MuiInputBase-input': {
                                backgroundColor: 'transparent !important',
                                },
                                '& .MuiInputBase-input:focus': {
                                backgroundColor: 'transparent !important',
                                },
                            }}
                        >
                            <MenuItem disabled={isMacDevice} value={0}>Desktop Add-in</MenuItem>
                            <MenuItem value={1}>Office Add-in</MenuItem>
                        </Select>
                    </Box>

                    </>}
                    
                    {hideProductOptions && <LoadingButton loading={!canSubmit} onClick={() => downloadLatestAsync(isDesktopDownload)} {...rest}>Download{beta ? "-Beta" : ""}</LoadingButton>}


                    {!hideChips && <Stack justifyContent='space-between' direction='row' mt={1}>
                        <Box mt={0}>
                            <Stack spacing={0} direction='row'>
                                {chips.map(c => <Chip key={c} size="small" label={c} />)}
                            </Stack>
                        </Box>
                        {!hideDetailsLink && <Link sx={{fontSize: '13px'}} component={NavLink} to="/addins">More info</Link>}


                    </Stack>}

            </Box>}
        </>
    )
}


export default DownloadsButton